import React from 'react';
import { Plus, Bell, Home, X, Zap } from 'lucide-react';

/**
 * BuildTopHeader
 * Slim browser-like top bar for the Build view.
 *
 * Left  : "Home" pill (back to BuildLanding) + tabs for open projects
 *         + small "+" button (new project = back to BuildLanding).
 * Right : credit balance chip · notification bell · user avatar.
 *
 * Pure UI — no business logic. All state/handlers are owned by MainLayout.
 */
export default function BuildTopHeader({
  tabs = [],
  activeTabId = null,
  onSelectTab,
  onCloseTab,
  onNewProject,
  onGoHome,
  user,
  credits = null,
  notifCount = 0,
  variant = 'dark',
}) {
  const isLight = variant === 'light';
  const palette = isLight
    ? {
        bg: '#FFFFFF',
        border: 'rgba(15,15,30,0.08)',
        pillBg: 'rgba(15,15,30,0.04)',
        pillBorder: 'rgba(15,15,30,0.08)',
        pillText: 'rgba(15,15,30,0.7)',
        pillTextHover: '#0F0F1E',
        pillBgHover: 'rgba(15,15,30,0.07)',
        tabInactiveBg: 'rgba(15,15,30,0.025)',
        tabInactiveBorder: 'rgba(15,15,30,0.07)',
        tabInactiveText: 'rgba(15,15,30,0.62)',
        tabInactiveHoverBg: 'rgba(15,15,30,0.06)',
        tabActiveBg: 'rgba(124,58,237,0.10)',
        tabActiveBorder: 'rgba(124,58,237,0.35)',
        tabActiveBorderTop: 'rgba(124,58,237,0.55)',
        tabActiveText: '#0F0F1E',
        tabDotInactive: 'rgba(15,15,30,0.22)',
        closeText: 'rgba(15,15,30,0.45)',
        closeHoverBg: 'rgba(15,15,30,0.08)',
        bellText: 'rgba(15,15,30,0.55)',
        bellHoverText: '#0F0F1E',
      }
    : {
        bg: '#0A0A10',
        border: 'rgba(255,255,255,0.06)',
        pillBg: 'rgba(255,255,255,0.04)',
        pillBorder: 'rgba(255,255,255,0.06)',
        pillText: 'rgba(255,255,255,0.7)',
        pillTextHover: '#fff',
        pillBgHover: 'rgba(255,255,255,0.07)',
        tabInactiveBg: 'rgba(255,255,255,0.03)',
        tabInactiveBorder: 'rgba(255,255,255,0.05)',
        tabInactiveText: 'rgba(255,255,255,0.65)',
        tabInactiveHoverBg: 'rgba(255,255,255,0.06)',
        tabActiveBg: 'rgba(124,58,237,0.10)',
        tabActiveBorder: 'rgba(124,58,237,0.30)',
        tabActiveBorderTop: 'rgba(124,58,237,0.45)',
        tabActiveText: '#fff',
        tabDotInactive: 'rgba(255,255,255,0.25)',
        closeText: 'rgba(255,255,255,0.4)',
        closeHoverBg: 'rgba(255,255,255,0.1)',
        bellText: 'rgba(255,255,255,0.5)',
        bellHoverText: '#fff',
      };
  return (
    <div
      data-testid="build-top-header"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        height: 44,
        padding: '0 12px',
        background: palette.bg,
        borderBottom: `1px solid ${palette.border}`,
        flexShrink: 0,
        userSelect: 'none',
      }}
    >
      {/* Home pill */}
      <button
        data-testid="build-header-home"
        onClick={onGoHome}
        title="Home"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 10px',
          background: palette.pillBg,
          border: `1px solid ${palette.pillBorder}`,
          color: palette.pillText,
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: 'inherit',
          transition: 'all 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.color = palette.pillTextHover; e.currentTarget.style.background = palette.pillBgHover; }}
        onMouseLeave={e => { e.currentTarget.style.color = palette.pillText; e.currentTarget.style.background = palette.pillBg; }}
      >
        <Home size={13} />
        Home
      </button>

      {/* Tabs */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, minWidth: 0, overflowX: 'auto' }}>
        {tabs.map(tab => {
          const active = tab.id === activeTabId;
          return (
            <div
              key={tab.id}
              data-testid={`build-tab-${tab.id}`}
              onClick={() => onSelectTab?.(tab)}
              title={tab.title}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px 6px 12px',
                maxWidth: 220,
                background: active ? palette.tabActiveBg : palette.tabInactiveBg,
                border: active ? `1px solid ${palette.tabActiveBorder}` : `1px solid ${palette.tabInactiveBorder}`,
                borderTop: active ? `1px solid ${palette.tabActiveBorderTop}` : `1px solid ${palette.tabInactiveBorder}`,
                color: active ? palette.tabActiveText : palette.tabInactiveText,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: active ? 600 : 500,
                fontFamily: 'inherit',
                transition: 'all 0.15s',
                flexShrink: 0,
              }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.background = palette.tabInactiveHoverBg; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.background = palette.tabInactiveBg; }}
            >
              {/* status dot */}
              <span style={{
                width: 6, height: 6, borderRadius: '50%',
                background: active ? '#7C3AED' : palette.tabDotInactive,
                boxShadow: active ? '0 0 6px rgba(124,58,237,0.6)' : 'none',
                flexShrink: 0,
              }} />
              <span style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 160,
              }}>
                {tab.title || 'Untitled'}
              </span>
              <button
                data-testid={`build-tab-close-${tab.id}`}
                onClick={e => { e.stopPropagation(); onCloseTab?.(tab); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 16, height: 16,
                  padding: 0,
                  background: 'transparent',
                  border: 'none',
                  color: palette.closeText,
                  cursor: 'pointer',
                  opacity: 0.6,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = palette.closeHoverBg; e.currentTarget.style.opacity = '1'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.opacity = '0.6'; }}
              >
                <X size={11} />
              </button>
            </div>
          );
        })}

        {/* New project (+) */}
        <button
          data-testid="build-header-new-project"
          onClick={onNewProject}
          title="New project"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28, height: 28,
            background: palette.pillBg,
            border: `1px solid ${palette.pillBorder}`,
            color: palette.pillText,
            cursor: 'pointer',
            fontFamily: 'inherit',
            transition: 'all 0.15s',
            flexShrink: 0,
            marginLeft: 4,
          }}
          onMouseEnter={e => { e.currentTarget.style.color = '#7C3AED'; e.currentTarget.style.borderColor = 'rgba(124,58,237,0.4)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = palette.pillText; e.currentTarget.style.borderColor = palette.pillBorder; }}
        >
          <Plus size={14} />
        </button>
      </div>

      {/* Right side */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {/* Credits */}
        {credits !== null && (
          <div
            data-testid="build-header-credits"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 10px',
              background: 'rgba(255,176,32,0.10)',
              border: '1px solid rgba(255,176,32,0.28)',
              color: '#B47A00',
              fontSize: 12,
              fontWeight: 700,
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            <Zap size={11} fill="#FFB020" stroke="#FFB020" />
            {credits}
          </div>
        )}

        {/* Bell */}
        <button
          data-testid="build-header-notifications"
          title="Notifications"
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 30, height: 30,
            background: 'transparent',
            border: 'none',
            color: palette.bellText,
            cursor: 'pointer',
            transition: 'color 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.color = palette.bellHoverText}
          onMouseLeave={e => e.currentTarget.style.color = palette.bellText}
        >
          <Bell size={15} />
          {notifCount > 0 && (
            <span style={{
              position: 'absolute', top: 5, right: 5,
              width: 7, height: 7, borderRadius: '50%',
              background: '#FF5F57',
              boxShadow: '0 0 6px rgba(255,95,87,0.7)',
            }} />
          )}
        </button>

        {/* Avatar */}
        <div
          data-testid="build-header-avatar"
          title={user?.name || 'User'}
          style={{
            width: 28, height: 28,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #FFB020, #FF6B6B)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#000', fontSize: 12, fontWeight: 700,
            cursor: 'pointer',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {user?.picture ? (
            <img src={user.picture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            (user?.name?.[0] || 'U').toUpperCase()
          )}
        </div>
      </div>
    </div>
  );
}
